import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Zap, Video, Calendar, Monitor, MonitorUp, Plus, ArrowRight } from "lucide-react"
import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { meetingsApi } from "../../services/meetings.api"
import { MEETING_ID_LENGTH } from "../../constants/meetings.constants"
import { shareDisplayNameProblem, type ShareNameProblem } from "../../utils/share-display-name"
import {
  classifyShareJoinError,
  describeShareJoinRefusal,
  shareCheckRefusal,
  shareRefusalField,
  type ShareJoinRefusal,
} from "@/features/screen-share-only/services/share-join"
import { screenShareOnlySupported } from "@/features/screen-share-only/services/share-capture"

interface ActionButton {
  id: string
  label: string
  mobileLabel: string
  description: string
  icon: React.ReactNode
  activeIcon: React.ReactNode
  gradient: string
  shadowColor: string
  onClick: () => void
}

export default function ModernActionButtons({ 
  onInstantClick,
  onJoinClick
}: { 
  onInstantClick: () => void
  onJoinClick: () => void
}) {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [pressedId, setPressedId] = useState<string | null>(null)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareName, setShareName] = useState("")
  const [shareNameError, setShareNameError] = useState<string | undefined>()
  const [shareMeetingId, setShareMeetingId] = useState("")
  const [shareError, setShareError] = useState<string | undefined>()
  // A refusal that is about neither field: full, ended, too many tries…
  const [shareFormError, setShareFormError] = useState<string | undefined>()
  const [shareChecking, setShareChecking] = useState(false)
  // Bumped when the dialog closes, so a check still in flight cannot navigate
  // somebody who has already walked away from it.
  const shareCheckRef = useRef(0)

  const [hideShareByDevice, setHideShareByDevice] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    const ua = navigator.userAgent
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0
    const isMobileUA = /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
    const isTabletUA = /iPad|Android(?!.*Mobile)/i.test(ua)
    // Also an iPad that calls itself a Mac, and any browser that cannot
    // capture a screen at all: the Screen Share page would have nothing to
    // offer them (screenShareOnlySupported).
    setHideShareByDevice(((isMobileUA || isTabletUA) && isTouchDevice) || !screenShareOnlySupported())
  }, [])

  // The id starts empty.
  //
  // This used to guess: the first upcoming or ongoing meeting in the list was
  // filled in for you. Guessing which meeting somebody means is a coin flip,
  // and a prefilled code reads as the right one — so the likeliest outcome was
  // sharing a screen into whichever meeting happened to sort first.

  const shareNameMessage = (problem: ShareNameProblem): string => {
    switch (problem) {
      case "NAME_REQUIRED": return t("meetings.errors.NAME_REQUIRED")
      case "NAME_TOO_SHORT": return t("meetings.errors.NAME_TOO_SHORT")
      case "NAME_TOO_LONG": return t("shareOnly.nameTooLong")
      case "NAME_INVALID_CHARS": return t("shareOnly.nameInvalidChars")
    }
  }

  /**
   * The name the share is shown under — the Screen Share rule, which the
   * backend applies too. The account's own name always passes as it is.
   */
  const validateShareName = (name: string): boolean => {
    const problem = shareDisplayNameProblem(name, user?.name)
    setShareNameError(problem ? shareNameMessage(problem) : undefined)
    return problem === null
  }

  /**
   * The id's shape only. Whether there is such a meeting is the server's to
   * say (`handleShareScreen`): this used to look the id up in this browser's
   * stored copy of meetings, which holds neither the meetings the dashboard
   * loads nor any recurring code, so real meetings were "not found".
   */
  const validateShareId = (id: string): boolean => {
    if (!id) {
      setShareError(t("meetings.errors.ID_REQUIRED"))
      return false
    }
    if (id.length !== MEETING_ID_LENGTH || !/^\d+$/.test(id)) {
      setShareError(t("meetings.errors.ID_INVALID_FORMAT"))
      return false
    }
    setShareError(undefined)
    return true
  }

  const handleShareIdChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, MEETING_ID_LENGTH)
    setShareMeetingId(digits)
    if (shareError) setShareError(undefined)
    if (shareFormError) setShareFormError(undefined)
  }

  /**
   * Ask the server, then go to the Screen Share page.
   *
   * The page joins the meeting as a screen only — no password, no camera, no
   * meeting UI — and starts the share from its own green button:
   * `getDisplayMedia` needs a click, and a navigation is not one.
   *
   * The check is the share-mode join with `check: true`: it says whether this
   * meeting can be shared into, in the same words the page would, and files no
   * lobby request. The typed name rides along, so a name the server will not
   * take is caught here rather than on the page.
   */
  const handleShareScreen = async () => {
    const nameOk = validateShareName(shareName)
    const idOk = validateShareId(shareMeetingId)
    if (!nameOk || !idOk) return
    const name = shareName.trim()
    const id = shareMeetingId
    const check = ++shareCheckRef.current
    setShareFormError(undefined)
    setShareChecking(true)
    let refusal: ShareJoinRefusal | null
    try {
      refusal = shareCheckRefusal(await meetingsApi.joinShare(id, { displayName: name, check: true }))
    } catch (err) {
      refusal = classifyShareJoinError(err)
    }
    if (check !== shareCheckRef.current) return
    setShareChecking(false)
    if (!refusal) {
      closeShareDialog(false)
      navigate(`/share/${id}?name=${encodeURIComponent(name)}`)
      return
    }
    const message = describeShareJoinRefusal(refusal, t, language)
    const field = shareRefusalField(refusal)
    if (field === "id") setShareError(message)
    else if (field === "name") setShareNameError(message)
    else setShareFormError(message)
  }

  // Opens with the account's name filled in: it is who they are everywhere
  // else, and most people share under it. They can still change it.
  const openShareDialog = () => {
    setShareName(user?.name ?? "")
    setShowShareDialog(true)
  }

  const closeShareDialog = (open: boolean) => {
    setShowShareDialog(open)
    if (!open) {
      // Reset state so re-opening the dialog feels clean.
      shareCheckRef.current += 1
      setShareChecking(false)
      setShareName("")
      setShareNameError(undefined)
      setShareMeetingId("")
      setShareError(undefined)
      setShareFormError(undefined)
    }
  }

  const buttons: ActionButton[] = [
    {
      id: "instant",
      label: t("meetings.instant"),
      mobileLabel: "Instant",
      description: t("meetings.startMeeting"),
      icon: <Zap className="w-6 h-6" />,
      activeIcon: <Plus className="w-6 h-6" />,
      gradient: "from-emerald-400 via-emerald-500 to-emerald-600",
      shadowColor: "rgba(16, 185, 129, 0.45)",
      onClick: onInstantClick
    },
    {
      id: "join",
      label: t("meetings.joinMeeting"),
      mobileLabel: "Join",
      description: t("meetings.joinMeeting"),
      icon: <Video className="w-6 h-6" />,
      activeIcon: <ArrowRight className="w-6 h-6" />,
      gradient: "from-primary via-primary-600 to-primary",
      shadowColor: "rgba(12, 90, 150, 0.45)",
      onClick: onJoinClick
    },
    {
      id: "schedule",
      label: t("meetings.scheduleMeeting"),
      mobileLabel: "Schedule",
      description: t("meetings.scheduleMeeting"),
      icon: <Calendar className="w-6 h-6" />,
      activeIcon: <ArrowRight className="w-6 h-6" />,
      gradient: "from-[#8B5CF6] via-[#7C3AED] to-[#6D28D9]",
      shadowColor: "rgba(139, 92, 246, 0.45)",
      onClick: () => navigate("/schedule")
    },
    {
      id: "share",
      label: t("meetings.screenShare"),
      mobileLabel: "Share",
      description: t("meetings.screenShare"),
      icon: <Monitor className="w-6 h-6" />,
      activeIcon: <ArrowRight className="w-6 h-6" />,
      gradient: "from-[#F59E0B] via-[#D97706] to-[#B45309]",
      shadowColor: "rgba(245, 158, 11, 0.45)",
      onClick: openShareDialog
    }
  ]

  const visibleButtons = useMemo(() => {
    if (!hideShareByDevice) return buttons
    return buttons.filter((b) => b.id !== "share")
  }, [buttons, hideShareByDevice])

  const handleClick = (btn: ActionButton) => {
    setPressedId(btn.id)
    setTimeout(() => {
      setPressedId(null)
      btn.onClick()
    }, 150)
  }

  return (
    <div className="w-full">
      <div
        className="grid gap-3 md:gap-4 lg:gap-3"
        style={{ gridTemplateColumns: `repeat(${visibleButtons.length}, minmax(0, 1fr))` }}
      >
        {visibleButtons.map((btn) => {
          const isHovered = hoveredId === btn.id
          const isPressed = pressedId === btn.id

          return (
            <button
              key={btn.id}
              id={`tour-action-${btn.id}`}
              onClick={() => handleClick(btn)}
              onMouseEnter={() => setHoveredId(btn.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`
                relative group flex w-full flex-col items-center gap-1 lg:gap-2.5 p-1.5 lg:p-4 
                rounded-xl lg:rounded-2xl lg:bg-card
                lg:border lg:border-stroke-100 dark:border-border dark:lg:border-border
                transition-all duration-300 ease-out
                hover:scale-[1.02]
                active:scale-[0.98]
                ${isPressed ? "scale-[0.95]" : ""}
              `}
            >
              {/* Icon Container with Gradient - shown on all screens */}
              <div 
                className={`
                  relative w-10 h-10 lg:w-11 lg:h-11 rounded-lg lg:rounded-2xl flex items-center justify-center
                  bg-gradient-to-br ${btn.gradient}
                  transition-all duration-300 ease-out
                  ${isHovered ? "scale-110 rotate-3" : "scale-100 rotate-0"}
                  ${isPressed ? "scale-95" : ""}
                `}
                style={{
                  boxShadow: isHovered 
                    ? `0 8px 20px ${btn.shadowColor}` 
                    : `0 4px 12px ${btn.shadowColor.replace("0.4", "0.3")}`
                }}
              >
                {/* White Icon */}
                <div className="text-white transition-transform duration-200">
                  <div className="w-6 h-6 lg:w-6 lg:h-6">
                    {isHovered ? btn.activeIcon : btn.icon}
                  </div>
                </div>

                {/* Shine Effect - desktop only */}
                <div 
                  className={`
                    hidden lg:block absolute inset-0 rounded-2xl overflow-hidden
                    ${isHovered ? "opacity-100" : "opacity-0"}
                    transition-opacity duration-300
                  `}
                >
                  <div 
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                    style={{
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)"
                    }}
                  />
                </div>
              </div>

              {/* Label */}
              <div className="flex flex-col items-center">
                <span className="hidden sm:block sm:pt-0 text-[13px] text-slate-800 dark:text-slate-200 text-center leading-tight">
                  {btn.label}
                </span>
                <span className="pt-2 font-semibold block sm:hidden sm:pt-0 text-[13px] text-slate-800 dark:text-slate-200 text-center leading-tight">
                  {btn.mobileLabel}
                </span>
              </div>

              {/* Desktop: Hover Border Glow */}
              <div 
                className={`
                  hidden lg:block absolute inset-0 rounded-2xl pointer-events-none
                  transition-opacity duration-300
                  ${isHovered ? "opacity-100" : "opacity-0"}
                `}
                style={{
                  background: `linear-gradient(135deg, ${btn.shadowColor.replace("0.4", "0.15")}, transparent)`,
                }}
              />
            </button>
          )
        })}
      </div>

      <Dialog open={showShareDialog} onOpenChange={closeShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('shareOnly.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('shareOnly.dialogDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">            <label className="text-xs font-medium text-muted-foreground">
                {t('meetings.name')}
              </label>
              <Input
                value={shareName}
                onChange={(e) => {
                  setShareName(e.target.value)
                  if (shareNameError) setShareNameError(undefined)
                  if (shareFormError) setShareFormError(undefined)
                }}
                onBlur={() => { if (shareName) validateShareName(shareName) }}
                placeholder={t('meetings.enterName')}
                autoComplete="off"
                variant="outlined"
                error={!!shareNameError}
                errorText={shareNameError}
                aria-label={t('meetings.name')}
              />
              <label className="text-xs font-medium text-muted-foreground">
                {t('meetings.screenShareId')}
              </label>
              <Input
                value={shareMeetingId}
                onChange={(e) => handleShareIdChange(e.target.value)}
                onBlur={() => {
                  if (shareMeetingId) validateShareId(shareMeetingId)
                }}
                placeholder={t('meetings.enterScreenShareId')}
                inputMode="numeric"
                type="tel"
                autoComplete="off"
                variant="outlined"
                error={!!shareError}
                errorText={shareError}
                aria-label={t('meetings.screenShareId')}
              />
            </div>

            {shareFormError && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2.5" role="alert">
                <p className="text-xs text-destructive">{shareFormError}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => closeShareDialog(false)}>
              {t('common.close')}
            </Button>
            <Button
              onClick={() => void handleShareScreen()}
              disabled={
                !shareName.trim() || !!shareNameError ||
                shareMeetingId.length !== MEETING_ID_LENGTH || !!shareError ||
                shareChecking
              }
              className="gap-2"
            >
              <MonitorUp className="w-4 h-4" />
              {shareChecking ? t('shareOnly.checking') : t('meetings.shareScreen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
